import dayjs from 'dayjs'
import PropTypes from 'prop-types'
import React from 'react'

import DealerClosedTag from '@/pages/RoIntake/components/DealerClosedTag'
import { formatCurrency } from '@/utils/formatters'

import { hoursInQueue } from './queue'
import RowActionCells from './RowActionCells'

/**
 * One Closed and submitted RO, waiting on the OEM (WARP-959). Worked in place since
 * WARP-966 (2026-10-08: "no modal is necessary"): Paid, Paid with adjustments, or back to
 * the Outstanding List, plus the dealer-closed flag and attachments, then Save.
 */
const PendingRow = ({
  dispositions,
  edit,
  now,
  onChange,
  onOpen,
  onSave,
  permissions,
  row,
  saving,
  showDealer,
}) => {
  const days = Math.floor(hoursInQueue(row, now) / 24)

  return (
    <tr className="border-charcoal-100 border-t align-middle">
      <td className="px-4 py-3">
        <span className="flex items-center gap-1.5">
          {/* The flag's own column is there for whoever can set it; others see the tag. */}
          {!permissions.canChangeStatus && row.dealerClosed && <DealerClosedTag />}
          {/* A submitted RO has no page of its own; it opens Manage Repair Order. */}
          <button
            className="text-charcoal-900 text-left font-mono text-sm font-semibold hover:text-blue-800 hover:underline"
            onClick={onOpen}
            type="button"
          >
            {row.roNumber}
          </button>
        </span>
        {showDealer && (
          <span className="text-charcoal-500 block truncate text-xs">{row.dealer.name}</span>
        )}
      </td>

      <td className="text-charcoal-700 whitespace-nowrap p-3 text-sm">
        {/* The year only when it is not this one. */}
        {dayjs(row.receivedAt).format(
          dayjs(row.receivedAt).isSame(now, 'year') ? 'MM/DD' : 'MM/DD/YY',
        )}
      </td>

      <td className="text-charcoal-600 whitespace-nowrap p-3 text-xs tabular-nums">
        {days} {days === 1 ? 'day' : 'days'}
      </td>

      <td className="text-charcoal-700 whitespace-nowrap p-3 text-right text-sm tabular-nums">
        {Number(row.totalClaimAmount) > 0 ? (
          formatCurrency(row.totalClaimAmount, false)
        ) : (
          <span className="text-charcoal-300">—</span>
        )}
      </td>

      <td className="text-charcoal-600 whitespace-nowrap p-3 text-xs">
        {dayjs(row.modifiedAt).format('MM/DD/YY h:mm A')}
      </td>

      <RowActionCells
        edit={edit}
        onChange={onChange}
        onSave={onSave}
        options={dispositions}
        permissions={permissions}
        row={row}
        saving={saving}
      />
    </tr>
  )
}

PendingRow.propTypes = {
  // The statuses a submitted RO can move to: `[{ value, label }]`.
  dispositions: PropTypes.arrayOf(
    PropTypes.shape({ label: PropTypes.string, value: PropTypes.string }),
  ).isRequired,
  edit: PropTypes.object.isRequired,
  now: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  // Opens Manage Repair Order for the row.
  onOpen: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  // What this user may do from the row; see `rowPermissions`.
  permissions: PropTypes.object.isRequired,
  row: PropTypes.object.isRequired,
  saving: PropTypes.bool,
  showDealer: PropTypes.bool,
}

PendingRow.defaultProps = {
  saving: false,
  showDealer: false,
}

export default PendingRow
